/**
 * Workflow type catalog for Approvals / ticket UI.
 *
 * Add a new entry here when shipping another workflow agent. Unknown types fall
 * back to a generic card that still supports approve/reject + history via the
 * shared workflow_stage / workflow_state.approval contract.
 */

export const WORKFLOW_TYPE_NUP = 'new_user_provisioning';
export const WORKFLOW_TYPE_TER = 'user_termination';
export const WORKFLOW_TYPE_PWD_RESET = 'password_reset';
export const WORKFLOW_TYPE_FFID = 'firefighter_id';
export const WORKFLOW_TYPE_ACCESS_MOD = 'access_modification';
export const WORKFLOW_TYPE_PAMTER = 'pam_offboarding';

const NUP_TEXT_MARKERS = [
  'new user provisioning',
  'provision a new user',
  'provision new user',
  'provision the new user',
  'onboard a new user',
  'onboard new user',
  'create a new sap user',
  'create new sap user',
  'set up a new user',
  'new hire',
];

const TER_TEXT_MARKERS = [
  'terminate user',
  'terminate the user',
  'user termination',
  'offboard',
  'offboarding',
  'decommission user',
  'deactivate user',
  'disable user',
  'delete user',
];

const PAMTER_TEXT_MARKERS = [
  'pam offboard',
  'pam offboarding',
  'pam health offboard',
  'pam health offboarding',
  'pamter',
  'pam ter',
  'shared mailbox offboard',
  'convert to shared mailbox',
];

const PWD_RESET_TEXT_MARKERS = [
  'reset password',
  'reset the password',
  'password reset',
  'reset my password',
  'forgot password',
  'forgot my password',
  'new password',
];

const FFID_TEXT_MARKERS = [
  'firefighter id',
  'firefighter-id',
  'emergency access',
  'grac_eam',
  'firefighter assignment',
];

const ACCESS_MOD_TEXT_MARKERS = [
  'access modification',
  'modify access',
  'change access',
  'update access',
  'access change',
  'role change',
  'add role',
  'remove role',
  'grant role',
  'revoke role',
  'additional access',
  'update roles',
  'modify roles',
];

/**
 * Frontend fallback used while asynchronous workflow metadata is still pending.
 * Mirrors the Security Orchestrator's deterministic NUP description rules.
 */
export function isNupTicketCandidate(ticket) {
  if (!ticket) return false;
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security') return false;

  const text = `${ticket.title || ''}\n${ticket.description || ''}`;
  const lowered = text.toLowerCase();
  if (PAMTER_TEXT_MARKERS.some((marker) => lowered.includes(marker))) return false;
  if (NUP_TEXT_MARKERS.some((marker) => lowered.includes(marker))) return true;

  const hasReferenceUser = /reference\s*user/i.test(text);
  const hasTargetUser =
    /^\s*(?:new\s*user|user(?:\s*id)?|userid|username|sap\s*user)\s*[:-]/im.test(text);
  return hasReferenceUser && hasTargetUser;
}

/**
 * Frontend fallback for PAM Health offboarding tickets.
 */
export function isPamterTicketCandidate(ticket) {
  if (!ticket) return false;
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security' && ticketType !== 'workflow') return false;

  const sub = String(ticket.sub_category || ticket.subCategory || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
  if (sub.includes('pam') && sub.includes('offboard')) return true;

  const text = `${ticket.title || ''}\n${ticket.description || ''}`;
  const lowered = text.toLowerCase();
  return PAMTER_TEXT_MARKERS.some((marker) => lowered.includes(marker));
}

/**
 * Frontend fallback for TER tickets while workflow metadata is still pending.
 */
export function isTerTicketCandidate(ticket) {
  if (!ticket) return false;
  if (isPamterTicketCandidate(ticket)) return false;
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security' && ticketType !== 'workflow') return false;

  const text = `${ticket.title || ''}\n${ticket.description || ''}`;
  const lowered = text.toLowerCase();
  if (TER_TEXT_MARKERS.some((marker) => lowered.includes(marker))) return true;

  const hasUser =
    /^\s*(?:username|user(?:\s*id)?|userid|sap\s*user)\s*[:-]/im.test(text);
  const hasDate =
    /^\s*(?:termination\s*date|terminate\s*(?:on|by|date)|effective\s*date)\s*[:-]/im.test(
      text,
    );
  return hasUser && hasDate;
}

/**
 * Frontend fallback for password-reset tickets while workflow metadata is still pending.
 */
export function isPwdResetTicketCandidate(ticket) {
  if (!ticket) return false;
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security' && ticketType !== 'workflow') return false;

  const text = `${ticket.title || ''}\n${ticket.description || ''}`;
  const lowered = text.toLowerCase();
  return PWD_RESET_TEXT_MARKERS.some((marker) => lowered.includes(marker));
}

export function isFfidTicketCandidate(ticket) {
  if (!ticket) return false;
  // Same shape as NUP/TER: never infer type from ticket_number (e.g. FFID_testing_*
  // can be a NUP test). Prefer explicit subcategory / workflow metadata; text is
  // only a lean-list fallback when workflow_state is still missing.
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security' && ticketType !== 'workflow') return false;
  const sub = String(ticket.sub_category || '').trim().toLowerCase();
  if (['firefighter id', 'ffid', 'firefighter', 'emergency access'].includes(sub)) {
    return true;
  }
  const text = `${ticket.title || ''}\n${ticket.description || ''}\n${ticket.description_preview || ''}`;
  const lowered = text.toLowerCase();
  return FFID_TEXT_MARKERS.some((marker) => lowered.includes(marker));
}

/**
 * Frontend fallback for Access Modification tickets while workflow metadata is
 * still pending. Mirrors workflow_orchestrator.py's _ACCESS_MOD_MARKERS.
 */
export function isAccessModTicketCandidate(ticket) {
  if (!ticket) return false;
  const ticketType = String(ticket.customization_type || ticket.category || '')
    .trim()
    .toLowerCase();
  if (ticketType !== 'security' && ticketType !== 'workflow') return false;

  const text = `${ticket.title || ''}\n${ticket.description || ''}`;
  const lowered = text.toLowerCase();
  return ACCESS_MOD_TEXT_MARKERS.some((marker) => lowered.includes(marker));
}

/** @typedef {{ key: string, label: string }} WorkflowFieldDef */

/**
 * @typedef {object} WorkflowTypeDef
 * @property {string} id
 * @property {string[]} aliases
 * @property {string} shortLabel  Badge text (e.g. NUP)
 * @property {string} label      Full name
 * @property {string} description
 * @property {string} approveVerb
 * @property {string} rejectVerb
 * @property {boolean} showsRoles
 * @property {boolean} showsSod
 * @property {boolean} showsApproval
 * @property {WorkflowFieldDef[]} primaryFields  Keys under workflow_state.extracted
 * @property {string} subjectField              extracted key used as "subject" in history
 */

/** @type {Record<string, WorkflowTypeDef>} */
export const WORKFLOW_TYPE_CATALOG = {
  [WORKFLOW_TYPE_NUP]: {
    id: WORKFLOW_TYPE_NUP,
    aliases: ['nup', 'new user provisioning', 'user provisioning'],
    shortLabel: 'NUP',
    label: 'New User Provisioning',
    description: 'Provision a SAP user by copying reference access',
    approveVerb: 'Approve provisioning',
    rejectVerb: 'Reject',
    showsRoles: true,
    showsSod: true,
    showsApproval: true,
    primaryFields: [
      { key: 'new_user', label: 'New user' },
      { key: 'last_name', label: 'Last name' },
      { key: 'email', label: 'Email' },
      { key: 'reference_user', label: 'Reference user' },
    ],
    subjectField: 'new_user',
  },
  [WORKFLOW_TYPE_TER]: {
    id: WORKFLOW_TYPE_TER,
    aliases: ['ter', 'user termination', 'termination'],
    shortLabel: 'TER',
    label: 'User Termination',
    description: 'Terminate a SAP user effective on a given date',
    approveVerb: 'Approve termination',
    rejectVerb: 'Reject',
    showsRoles: false,
    showsSod: false,
    showsApproval: true,
    primaryFields: [
      { key: 'username', label: 'Username' },
      { key: 'termination_date', label: 'Termination date' },
      { key: 'termination_mode', label: 'Mode' },
    ],
    subjectField: 'username',
  },
  [WORKFLOW_TYPE_PAMTER]: {
    id: WORKFLOW_TYPE_PAMTER,
    aliases: ['pamter', 'pam offboarding', 'pam health offboarding', 'pam_offboarding'],
    shortLabel: 'PAMTER',
    label: 'PAM Health Offboarding',
    description: 'Convert mailbox to shared and disable the Entra account',
    approveVerb: 'Approve offboarding',
    rejectVerb: 'Reject',
    showsRoles: false,
    showsSod: false,
    showsApproval: false,
    primaryFields: [
      { key: 'username', label: 'User' },
      { key: 'forwarding_recipient', label: 'Forward to' },
    ],
    subjectField: 'username',
  },
  [WORKFLOW_TYPE_PWD_RESET]: {
    id: WORKFLOW_TYPE_PWD_RESET,
    aliases: ['pwd_reset', 'password reset', 'reset password'],
    shortLabel: 'PWD',
    label: 'Password Reset',
    description: 'Reset a SAP user password and email the new one',
    approveVerb: 'Approve reset',
    rejectVerb: 'Reject',
    showsRoles: false,
    showsSod: false,
    // Password reset runs straight through once triggered — no approval gate,
    // matching pwd_reset.py's workflow_state (never carries a sod/approval key).
    showsApproval: false,
    primaryFields: [
      { key: 'username', label: 'Username' },
      { key: 'email', label: 'Email' },
    ],
    subjectField: 'username',
  },
  [WORKFLOW_TYPE_FFID]: {
    id: WORKFLOW_TYPE_FFID,
    aliases: ['ffid', 'firefighter', 'firefighter id', 'emergency access'],
    shortLabel: 'FFID',
    label: 'Firefighter ID',
    description: 'Temporary GRC Firefighter ID assignment',
    approveVerb: 'Approve FFID request',
    rejectVerb: 'Reject',
    showsRoles: false,
    showsSod: false,
    showsApproval: true,
    primaryFields: [
      { key: 'requester_name', label: 'Requester' },
      { key: 'firefighter_sap_user', label: 'Firefighter SAP user' },
      { key: 'reason', label: 'Reason / activity' },
      { key: 'tcodes', label: 'Requested T-codes' },
      { key: 'access_window', label: 'Access window' },
      { key: 'proposed_ffid', label: 'Proposed FFID' },
      { key: 'availability', label: 'FFID availability' },
      { key: 'tcode_check', label: 'T-code / access validation' },
    ],
    subjectField: 'firefighter_sap_user',
  },
  [WORKFLOW_TYPE_ACCESS_MOD]: {
    id: WORKFLOW_TYPE_ACCESS_MOD,
    aliases: ['access_mod', 'access modification', 'modify access'],
    shortLabel: 'ACCESS',
    label: 'Access Modification',
    description: 'Grant and/or revoke SAP roles for an existing user',
    approveVerb: 'Approve change',
    rejectVerb: 'Reject',
    showsRoles: true,
    showsSod: false,
    showsApproval: true,
    primaryFields: [
      { key: 'target_user', label: 'Target user' },
      { key: 'mode', label: 'Mode' },
      { key: 'reference_user', label: 'Reference user' },
      { key: 'roles_add', label: 'Roles to add' },
      { key: 'roles_remove', label: 'Roles to remove' },
    ],
    subjectField: 'target_user',
  },
};

const GENERIC_WORKFLOW_TYPE = {
  id: 'workflow',
  aliases: [],
  shortLabel: 'Workflow',
  label: 'Workflow',
  description: 'Human-gated workflow request',
  approveVerb: 'Approve',
  rejectVerb: 'Reject',
  showsRoles: false,
  showsSod: false,
  showsApproval: true,
  primaryFields: [],
  subjectField: null,
};

export function normalizeWorkflowTypeId(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

/** Parse workflow_state whether the API returned an object or JSON string. */
export function parseWorkflowState(ticket) {
  const state = ticket?.workflow_state;
  if (state && typeof state === 'object') return state;
  if (typeof state === 'string') {
    try {
      const parsed = JSON.parse(state);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * Resolve the workflow type definition for a ticket.
 * Prefer agent-produced workflow metadata, then description heuristics.
 */
export function resolveWorkflowType(ticket) {
  const state = parseWorkflowState(ticket);
  const rawFromState =
    state.workflow_agent || state.workflow_type || state.workflowType || ticket?.workflow_agent || '';
  const candidates = [rawFromState]
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean);

  for (const def of Object.values(WORKFLOW_TYPE_CATALOG)) {
    const idNorm = normalizeWorkflowTypeId(def.id);
    const aliasSet = new Set([
      idNorm,
      def.shortLabel.toLowerCase(),
      ...def.aliases.map((a) => a.toLowerCase()),
      ...def.aliases.map((a) => normalizeWorkflowTypeId(a)),
    ]);
    for (const c of candidates) {
      const cNorm = normalizeWorkflowTypeId(c);
      // Exact matches only — never substring (e.g. "pamter" must not match TER "ter").
      if (aliasSet.has(c) || aliasSet.has(cNorm)) {
        return def;
      }
    }
  }

  if (isPamterTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_PAMTER];
  }

  if (isNupTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_NUP];
  }

  if (isTerTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_TER];
  }

  if (isPwdResetTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_PWD_RESET];
  }

  if (isFfidTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_FFID];
  }

  if (isAccessModTicketCandidate(ticket)) {
    return WORKFLOW_TYPE_CATALOG[WORKFLOW_TYPE_ACCESS_MOD];
  }

  if (rawFromState) {
    return {
      ...GENERIC_WORKFLOW_TYPE,
      id: normalizeWorkflowTypeId(rawFromState),
      shortLabel: String(rawFromState)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase())
        .slice(0, 24),
      label: String(rawFromState).replace(/_/g, ' '),
    };
  }

  return GENERIC_WORKFLOW_TYPE;
}

/**
 * Register (or replace) a workflow type at runtime — useful for tests / plugins.
 * @param {WorkflowTypeDef} def
 */
export function registerWorkflowType(def) {
  if (!def?.id) throw new Error('registerWorkflowType requires def.id');
  WORKFLOW_TYPE_CATALOG[def.id] = def;
  return def;
}
